import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Loader2, 
  ArrowLeft,
  User,
  Briefcase,
  MapPin,
  Calendar,
  Mail,
  Copy,
  ExternalLink,
  CheckCircle2,
  Clock,
  FileText
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCandidateDetail() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState(null);
  const [responses, setResponses] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchCandidate();
  }, [candidateId, navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
  });

  const fetchCandidate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/admin/candidate/${candidateId}`,
        getAuthHeaders()
      );
      setCandidate(response.data.candidate);
      setResponses(response.data.responses);
    } catch (err) {
      console.error("Error fetching candidate:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin/login");
      } else if (err.response?.status === 404) {
        toast.error("Candidate not found");
        navigate("/admin");
      } else {
        toast.error("Failed to load candidate data");
      }
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/assessment/${candidate.uniqueToken}`;
    navigator.clipboard.writeText(url);
    toast.success("Assessment link copied!");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
      case "started":
        return <Badge className="bg-blue-100 text-blue-700">In Progress</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#003366] mx-auto mb-4" />
          <p className="text-muted-foreground">Loading candidate details...</p>
        </div>
      </div>
    );
  }

  if (!candidate) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky-header py-4 px-6 bg-white">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/admin" 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyLink} className="gap-2">
              <Copy className="w-4 h-4" />
              Copy Link
            </Button>
            <Link to={`/assessment/${candidate.uniqueToken}`} target="_blank">
              <Button variant="outline" size="sm" className="gap-2">
                <ExternalLink className="w-4 h-4" />
                View Assessment
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-6">
        {/* Candidate Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-[#003366] font-['Manrope']">
                {candidate.fullName}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  {candidate.jobTitle || "—"}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {candidate.location || "—"}
                </span>
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {candidate.email}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(candidate.status)}
              <span className="text-sm text-muted-foreground">
                {candidate.progressPercentage}% complete
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-[#003366] text-lg">Profile Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Department</p>
                <p className="font-medium">{candidate.department || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Business Unit</p>
                <p className="font-medium">{candidate.businessUnit || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Job Grade</p>
                <p className="font-medium">{candidate.jobGrade || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Hire Date</p>
                <p className="font-medium">{candidate.hireDate || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Manager</p>
                <p className="font-medium">{candidate.managerName || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Department Head</p>
                <p className="font-medium">{candidate.departmentHead || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Performance Rating</p>
                <p className="font-medium">{candidate.performanceRating || "—"}</p>
              </div>
            </CardContent>
          </Card>

          {/* Responses Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-[#003366] text-lg">Assessment Responses</CardTitle>
              <CardDescription>
                View submitted responses by section
              </CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(responses).length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No responses submitted yet</p>
                </div>
              ) : (
                <Tabs defaultValue={Object.keys(responses)[0]} className="w-full">
                  <TabsList className="w-full justify-start flex-wrap h-auto gap-1 bg-transparent p-0 mb-4">
                    {Object.keys(responses).map((sectionId) => (
                      <TabsTrigger 
                        key={sectionId} 
                        value={sectionId}
                        className="data-[state=active]:bg-[#003366] data-[state=active]:text-white rounded-full px-4"
                      >
                        {sectionId.replace("-", " ").toUpperCase()}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {Object.entries(responses).map(([sectionId, sectionResponses]) => (
                    <TabsContent key={sectionId} value={sectionId}>
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-4">
                          {Object.entries(sectionResponses).map(([questionId, response]) => (
                            <div 
                              key={questionId} 
                              className="p-4 rounded-lg bg-slate-50 border"
                            >
                              <p className="text-sm font-medium text-[#003366] mb-2">
                                Question: {questionId}
                              </p>
                              <div className="text-sm">
                                {typeof response === 'object' ? (
                                  <pre className="whitespace-pre-wrap text-muted-foreground">
                                    {JSON.stringify(response, null, 2)}
                                  </pre>
                                ) : (
                                  <p className="text-muted-foreground">{String(response)}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Technologies */}
        {candidate.technologies && candidate.technologies.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-[#003366] text-lg">Technology Environment</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {candidate.technologies.map((tech, index) => (
                  <Badge key={index} variant="outline" className="px-3 py-1">
                    {tech.name}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
